import React, { useEffect } from 'react'
import {
  Grid,
  TextField,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  Box,
  Autocomplete,
  ListItemText,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import Asterisk from '../../../components/Asterisk'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  selectLocationById,
  selectLocations,
  selectSelectedLocationId,
} from '../../location/locationSlice'
import { getLocationAddress } from '../../patients/utils'
import {
  dataTypes,
  ERROR_MESSAGES,
  patientDetails,
  patientDischargeSummmary,
  physicalExamFields,
  physicalInvestigation,
} from './Constants'
import { useLocation } from 'react-router-dom'
import dayjs from 'dayjs'
import AddIcon from '@mui/icons-material/Add'
import PatientDischargeSummaryPdf from './PatientDischargeSummaryPdf'
import { pdf } from '@react-pdf/renderer'
import { startSxpProxy } from '../../../utils/api'
import { ADMIN_PROJECT_ID } from '../../../utils/constants'
import {
  fetchSlotsDoctorsAsync,
  selectSlotsDoctors,
} from '../../slots/slotsSlice'

const styles = () => ({
  dischargeFormField: {
    // display: 'flex',
    // flexDirection: 'column',
    // gap: 10,
    fontSize: '12px',
    fontWeight: '600',
  },
  commonBtn: {
    background: '#4682b4',
    textTransform: 'capitalize',
  },
  medicationTable: {
    border: '1px solid #ddd',
    borderCollapse: 'collapse',
    '& th, & td': {
      border: '1px solid #ddd',
      textAlign: 'center',
      padding: '8px',
    },
    '& th': {
      backgroundColor: '#4682b4',
      color: 'white',
      fontWeight: 'bold',
    },
  },
})

export interface FormValues {
  locationAddress: string
  name: string
  ageGender: string
  doctor: string
  department: string
  ratePlan: string
  mrNo: string
  visitId: string
  admissionDate: string
  dischargeDate: string
  wardBed: string
  source: string
  finalDiagnosis: string
  history: string
  investigationsDone: string
  treatmentGiven: string
  progressInHospital: string
  treatmentAdvised: string
  preventiveCare: string
  diet: string
  daysAfter: string
  symptoms: string[]
  preparedByDoctor: string
  preparedByDoctorSignature: string
  preparedByDoctorRegNo: string
  preparedByDate: string
  treatingDoctor: string
  treatingDoctorRegNo: string
  treatingDoctorSignature: string
  oe: string
  bp: string
  pr: string
  spo2: string
  temp: string
  cvs: string
  rs: string
  pa: string
  cns: string
  medications: Medication[]
}

interface Medication {
  sno: number
  medicine: string
  morning: string
  afternoon: string
  evening: string
  noOfDays: string
  remarks: string
}

const validationSchema = Yup.object({
  name: Yup.string().required(ERROR_MESSAGES.name),
  ageGender: Yup.string().required(ERROR_MESSAGES.ageGender),
  doctor: Yup.string().required(ERROR_MESSAGES.doctor),
  department: Yup.string().required(ERROR_MESSAGES.department),
  ratePlan: Yup.string().required(ERROR_MESSAGES.ratePlan),
  mrNo: Yup.string().required(ERROR_MESSAGES.mrNo),
  visitId: Yup.string().required(ERROR_MESSAGES.visitId),
  admissionDate: Yup.date().required(ERROR_MESSAGES.admissionDate),
  dischargeDate: Yup.date().required(ERROR_MESSAGES.dischargeDate),
  wardBed: Yup.string().required(ERROR_MESSAGES.wardBed),
  source: Yup.string().required(ERROR_MESSAGES.source),
  finalDiagnosis: Yup.string().required(ERROR_MESSAGES.finalDiagnosis),
  history: Yup.string().required(ERROR_MESSAGES.history),
  investigationsDone: Yup.string().required(ERROR_MESSAGES.investigationsDone),
  treatmentGiven: Yup.string().required(ERROR_MESSAGES.treatmentGiven),
  preventiveCare: Yup.string().required(ERROR_MESSAGES.preventiveCare),
  diet: Yup.string().required(ERROR_MESSAGES.diet),
  daysAfter: Yup.string().required(ERROR_MESSAGES.daysAfter),
  symptoms: Yup.array().of(Yup.string().required(ERROR_MESSAGES.symptom)),
  preparedByDoctor: Yup.string().required(ERROR_MESSAGES.preparedByDoctor),
  preparedByDoctorRegNo: Yup.string(),
  preparedByDoctorSignature: Yup.string(),
  preparedByDate: Yup.string().required(ERROR_MESSAGES.preparedByDate),
  treatingDoctor: Yup.string().required(ERROR_MESSAGES.treatingDoctor),
  treatingDoctorRegNo: Yup.string(),
  treatingDoctorSignature: Yup.string(),
  oe: Yup.string(),
  bp: Yup.string(),
  pr: Yup.string(),
  spo2: Yup.string(),
  temp: Yup.string(),
  cvs: Yup.string(),
  rs: Yup.string(),
  pa: Yup.string(),
  cns: Yup.string(),
  medications: Yup.array().of(
    Yup.object({
      sno: Yup.number().required(),
      medicine: Yup.string(),
      morning: Yup.string(),
      afternoon: Yup.string(),
      evening: Yup.string(),
      noOfDays: Yup.string(),
      remarks: Yup.string(),
    })
  ),
  previewDialog: Yup.boolean(),
})

const PatientDetailsForm = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  const location = useLocation()
  const state = location.state || {}
  const doctors = useAppSelector(selectSlotsDoctors)
  const dispatch = useAppDispatch()

  const selectedLoc = useAppSelector(selectSelectedLocationId)
  const selectedLocID = useAppSelector((state) =>
    selectLocationById(selectedLoc, state)
  )
  const prefixIdentifier = selectedLocID?.resource?.identifier?.find(
    (item: { system?: string; value?: string }) => item?.system === 'prefix'
  )
  const locations = useAppSelector(selectLocations)

  const computedLocationAddress =
    getLocationAddress(prefixIdentifier?.value || '', locations)?.address
      ?.line?.[0] || ''

  const initialValues: FormValues = {
    name: state?.patientName || '',
    ageGender: state?.patientAge + state?.patientGender || '',
    doctor: state?.assigned_doctor || '',
    department: state?.doctor_department || '',
    ratePlan: '',
    mrNo: state?.patientId || '',
    visitId: '',
    admissionDate: dayjs(state?.dateStarted).format('YYYY-MM-DD') || '',
    dischargeDate: '',
    wardBed: state?.bedNumber || '',
    source: '',
    finalDiagnosis: '',
    history: '',
    investigationsDone: '',
    treatmentGiven: '',
    progressInHospital: '',
    treatmentAdvised: '',
    preventiveCare: '',
    diet: '',
    daysAfter: '',
    symptoms: [''],
    preparedByDoctor: '',
    preparedByDoctorSignature: '',
    preparedByDoctorRegNo: '',
    preparedByDate: '',
    treatingDoctor: '',
    treatingDoctorRegNo: '',
    treatingDoctorSignature: '',
    oe: '',
    bp: '',
    pr: '',
    spo2: '',
    temp: '',
    cvs: '',
    rs: '',
    pa: '',
    cns: '',
    locationAddress: computedLocationAddress || '',
    medications: [
      {
        sno: 1,
        medicine: '',
        morning: '',
        afternoon: '',
        evening: '',
        noOfDays: '',
        remarks: '',
      },
    ],
  }

  const formik: any = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema,
    onSubmit: (values) => {
      console.log(values, computedLocationAddress, 'Form Submitted')
    },
  })

  const handleMedicationChange = (
    index: number,
    field: keyof Medication,
    value: string
  ) => {
    const meds = [...formik.values.medications]
    meds[index] = { ...meds[index], [field]: value }
    formik.setFieldValue('medications', meds)
  }

  const handleAddRow = () => {
    const meds = formik?.values?.medications
    formik.setFieldValue('medications', [
      ...meds,
      {
        sno: meds.length + 1,
        medicine: '',
        morning: '',
        afternoon: '',
        evening: '',
        noOfDays: '',
        remarks: '',
      },
    ])
  }

  const openPdfInNewTab = async (formValues: any) => {
    const doc = <PatientDischargeSummaryPdf formValues={formValues} />
    const blob = await pdf(doc).toBlob()

    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
  }

  const getDoctorDetails = (id: string, field: string) => {
    startSxpProxy(ADMIN_PROJECT_ID, 'getDoctorById', { id })
      .then((data) => {
        const practitioner = data?.data?.entry?.find(
          (e: { resource: { resourceType: string } }) =>
            e.resource.resourceType === 'Practitioner'
        )
        const regNumber =
          practitioner?.resource?.qualification?.[0]?.identifier?.[0]?.value ??
          '-'

        const signature = practitioner?.resource?.photo?.[1]?.url
        formik?.setFieldValue(`${field}Signature`, signature)
        formik?.setFieldValue(`${field}RegNo`, regNumber)
      })
      .catch((err) => console.log(err))
  }

  const handleDoctorChange = (name: string, value: string) => {
    formik?.setFieldValue(name, value)
    getDoctorDetails(value, name)
  }

  useEffect(() => {
    dispatch(fetchSlotsDoctorsAsync())
  }, [dispatch])

  // useEffect(() => {
  //   formik.setValues(prefillData || initialValues)
  //   // eslint-disable-next-line
  // }, [prefillData])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant='h6'>Patient Details</Typography>
      </Grid>
      {patientDetails?.map(({ label, name, type }) => (
        <Grid
          key={name}
          item
          xs={12}
          sm={6}
          md={3}
          className={classes.dischargeFormField}
        >
          <label>
            {label}
            <Asterisk />
          </label>
          <TextField
            size='small'
            name={name}
            type={type || 'text'}
            fullWidth
            margin='normal'
            value={formik?.values?.[name as keyof FormValues]}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            error={Boolean(
              formik?.touched?.[name as keyof FormValues] &&
                formik?.errors?.[name as keyof FormValues]
            )}
            helperText={
              formik?.touched?.[name as keyof FormValues] &&
              typeof formik?.errors?.[name as keyof FormValues] ===
                dataTypes?.STRING
                ? (formik?.errors?.[name as keyof FormValues] as string)
                : ''
            }
            InputLabelProps={
              type === dataTypes.DATE ? { shrink: true } : undefined
            }
          />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Typography variant='h6'>Discharge Summary</Typography>
      </Grid>
      {patientDischargeSummmary?.map(({ label, name }) => (
        <Grid
          key={name}
          item
          xs={12}
          sm={6}
          md={6}
          className={classes.dischargeFormField}
        >
          <label>
            {label}:<Asterisk />
          </label>
          <TextField
            name={name}
            multiline
            rows={3}
            fullWidth
            margin='normal'
            value={formik?.values?.[name as keyof FormValues]}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            error={Boolean(
              formik?.touched?.[name as keyof FormValues] &&
                formik?.errors?.[name as keyof FormValues]
            )}
            helperText={
              formik?.touched?.[name as keyof FormValues] &&
              typeof formik?.errors?.[name as keyof FormValues] ===
                dataTypes?.STRING
                ? (formik?.errors?.[name as keyof FormValues] as string)
                : ''
            }
          />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Typography fontFamily={'inherit'} fontSize={'12px'} fontWeight={600}>
          Physical Examination:
        </Typography>
      </Grid>
      {physicalExamFields?.map(({ label, name }) => (
        <Grid
          key={name}
          item
          xs={12}
          sm={6}
          md={3}
          className={classes.dischargeFormField}
        >
          <label>
            {label}
            <Asterisk />
          </label>
          <TextField
            name={name}
            size='small'
            fullWidth
            margin='normal'
            value={formik?.values?.[name as keyof FormValues] || ''}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            error={Boolean(
              formik?.touched?.[name as keyof FormValues] &&
                formik?.errors?.[name as keyof FormValues]
            )}
            helperText={
              formik?.touched?.[name as keyof FormValues] &&
              typeof formik?.errors?.[name as keyof FormValues] ===
                dataTypes?.STRING
                ? (formik?.errors?.[name as keyof FormValues] as string)
                : ''
            }
          />
        </Grid>
      ))}

      <Grid item xs={12}>
        {/* <Typography variant='h6'>Physical Examination:</Typography> */}
      </Grid>

      {physicalInvestigation?.map(({ label, name }) => (
        <Grid
          key={name}
          item
          xs={12}
          sm={6}
          md={6}
          className={classes.dischargeFormField}
        >
          <label>
            {label}
            <Asterisk />
          </label>
          <TextField
            name={name}
            size='small'
            multiline
            rows={3}
            fullWidth
            margin='normal'
            value={formik?.values?.[name as keyof FormValues] || ''}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            error={Boolean(
              formik?.touched?.[name as keyof FormValues] &&
                formik?.errors?.[name as keyof FormValues]
            )}
            helperText={
              formik?.touched?.[name as keyof FormValues] &&
              typeof formik?.errors?.[name as keyof FormValues] ===
                dataTypes?.STRING
                ? (formik?.errors?.[name as keyof FormValues] as string)
                : ''
            }
          />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Typography variant='h6'>Discharge Medications Advised:</Typography>
        <Table className={classes?.medicationTable}>
          <TableHead>
            <TableRow>
              <TableCell>Sno</TableCell>
              <TableCell>Medicine</TableCell>
              <TableCell>Morning</TableCell>
              <TableCell>Afternoon</TableCell>
              <TableCell>Evening</TableCell>
              <TableCell>No. of Days</TableCell>
              <TableCell>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formik?.values?.medications?.map(
              (medication: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{medication?.sno}</TableCell>
                  <TableCell>
                    <TextField
                      size='small'
                      name={`medications[${index}].medicine`}
                      value={medication?.medicine}
                      onChange={(e) =>
                        handleMedicationChange(
                          index,
                          'medicine',
                          e.target.value
                        )
                      }
                      fullWidth
                      error={
                        formik?.touched?.medications?.[index]?.medicine &&
                        Boolean(formik?.errors?.medications?.[index]?.medicine)
                      }
                      helperText={
                        formik?.touched?.medications?.[index]?.medicine &&
                        formik?.errors?.medications?.[index]?.medicine
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size='small'
                      name={`medications[${index}].morning`}
                      value={medication?.morning}
                      onChange={(e) =>
                        handleMedicationChange(index, 'morning', e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size='small'
                      name={`medications[${index}].afternoon`}
                      value={medication?.afternoon}
                      onChange={(e) =>
                        handleMedicationChange(
                          index,
                          'afternoon',
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size='small'
                      name={`medications[${index}].evening`}
                      value={medication?.evening}
                      onChange={(e) =>
                        handleMedicationChange(index, 'evening', e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size='small'
                      name={`medications[${index}].noOfDays`}
                      value={medication?.noOfDays}
                      onChange={(e) =>
                        handleMedicationChange(
                          index,
                          'noOfDays',
                          e.target.value
                        )
                      }
                      fullWidth
                      error={
                        formik?.touched?.medications?.[index]?.noOfDays &&
                        Boolean(formik?.errors?.medications?.[index]?.noOfDays)
                      }
                      helperText={
                        formik?.touched?.medications?.[index]?.noOfDays &&
                        formik?.errors?.medications?.[index]?.noOfDays
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size='small'
                      name={`medications[${index}].remarks`}
                      value={medication?.remarks}
                      onChange={(e) =>
                        handleMedicationChange(index, 'remarks', e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
        <Box mt={2}>
          <Button
            className={classes?.commonBtn}
            variant='contained'
            size='small'
            onClick={handleAddRow}
            startIcon={<AddIcon />}
          >
            Add Row
          </Button>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant='h6'>Follow-Up / Review:</Typography>
        <Box display='flex' alignItems='center' gap={2} mb={2} mt={2}>
          <Typography>Reviewed after</Typography>
          <TextField
            name='daysAfter'
            value={formik?.values?.daysAfter}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            error={
              formik?.touched?.daysAfter && Boolean(formik?.errors?.daysAfter)
            }
            helperText={formik?.touched?.daysAfter && formik?.errors?.daysAfter}
            variant='outlined'
            size='small'
          />
          <Typography>days, in</Typography>
          <TextField
            name='department'
            value={formik?.values?.department}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            error={
              formik?.touched?.department && Boolean(formik?.errors?.department)
            }
            helperText={
              formik?.touched?.department && formik?.errors?.department
            }
            variant='outlined'
            size='small'
          />
        </Box>

        <Typography>
          Symptoms of Emergency: (If Observed, please report to Emergency Dept.
          (Casualty))
        </Typography>
        {formik?.values?.symptoms?.map((symptom: any, index: number) => (
          <Box
            key={index}
            display='flex'
            alignItems='center'
            gap={2}
            mb={1}
            mt={1}
          >
            <Typography>{index + 1}.</Typography>
            <TextField
              name={`symptoms[${index}]`}
              value={symptom}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              error={
                formik?.touched?.symptoms?.[index] &&
                Boolean(formik?.errors?.symptoms?.[index])
              }
              helperText={
                formik?.touched?.symptoms?.[index] &&
                formik?.errors?.symptoms?.[index]
              }
              variant='outlined'
              size='small'
            />
          </Box>
        ))}

        <Grid item xs={2} mt={2}>
          <Button
            className={classes?.commonBtn}
            size='small'
            variant='contained'
            onClick={() =>
              formik.setFieldValue('symptoms', [
                ...(formik?.values?.symptoms || []),
                '',
              ])
            }
            startIcon={<AddIcon />}
          >
            Add Row
          </Button>
        </Grid>
      </Grid>

      {/* <Grid
        item
        xs={12}
        sm={6}
        md={6}
        lg={6}
        xl={6}
        className={classes?.dischargeFormField}
      >
        <label>
          Symptoms of emergency
          <Asterisk />
        </label>
        <TextField
          name='symptomsOfEmergency'
          multiline
          rows={3}
          fullWidth
          margin='normal'
          value={formik?.values?.symptomsOfEmergency}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          error={
            formik?.touched?.symptomsOfEmergency &&
            Boolean(formik?.errors?.symptomsOfEmergency)
          }
          helperText={
            formik?.touched?.symptomsOfEmergency &&
            formik?.errors?.symptomsOfEmergency
          }
        />
      </Grid> */}

      <Grid item xs={12}>
        <Typography variant='h6'>Prepared By:</Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={4} className={classes.dischargeFormField}>
        <label>
          Dr.
          <Asterisk />
        </label>
        <Autocomplete
          fullWidth
          disableClearable
          value={doctors?.find(
            (doctor) => doctor?.id === formik?.treatingDoctor
          )}
          options={doctors}
          getOptionLabel={(option) => option?.name}
          onChange={(event, value) =>
            handleDoctorChange('preparedByDoctor', value ? value.id : '')
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              placeholder='Select Doctors Name'
              margin='normal'
            />
          )}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderOption={(props, item) => (
            <li {...props} key={item?.id}>
              <ListItemText>{item?.name}</ListItemText>
            </li>
          )}
        />
        {/* <TextField
          name='preparedByDoctor'
          fullWidth
          margin='normal'
          size='small'
          value={formik?.values?.preparedByDoctor}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          error={
            formik?.touched?.preparedByDoctor &&
            Boolean(formik?.errors?.preparedByDoctor)
          }
          helperText={
            formik?.touched?.preparedByDoctor &&
            typeof formik?.errors?.preparedByDoctor === 'string'
              ? (formik?.errors?.preparedByDoctor as string)
              : ''
          }
        /> */}
      </Grid>
      {/* <Grid item xs={12} sm={6} md={4} className={classes.dischargeFormField}>
        <label>
          Signature
          <Asterisk />
        </label>
        <TextField
          name='preparedByDoctorSignature'
          fullWidth
          margin='normal'
          size='small'
          value={formik?.values?.preparedByDoctorSignature}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          error={
            formik?.touched?.preparedByDoctorSignature &&
            Boolean(formik?.errors?.preparedByDoctorSignature)
          }
          helperText={
            formik?.touched?.preparedByDoctorSignature &&
            typeof formik?.errors?.preparedByDoctorSignature === 'string'
              ? (formik?.errors?.preparedByDoctorSignature as string)
              : ''
          }
        />
      </Grid> */}
      <Grid item xs={12} sm={6} md={4} className={classes.dischargeFormField}>
        <label>
          Date
          <Asterisk />
        </label>
        <TextField
          name='preparedByDate'
          type='date'
          fullWidth
          margin='normal'
          size='small'
          InputLabelProps={{ shrink: true }}
          value={formik?.values?.preparedByDate}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          error={
            formik?.touched?.preparedByDate &&
            Boolean(formik?.errors?.preparedByDate)
          }
          helperText={
            formik?.touched?.preparedByDate &&
            typeof formik?.errors?.preparedByDate === dataTypes?.STRING
              ? (formik?.errors?.preparedByDate as string)
              : ''
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant='h6'>Treating Doctor:</Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={4} className={classes.dischargeFormField}>
        <label>
          Dr.
          <Asterisk />
        </label>
        <Autocomplete
          fullWidth
          disableClearable
          value={doctors?.find(
            (doctor) => doctor?.id === formik?.treatingDoctor
          )}
          options={doctors}
          getOptionLabel={(option) => option?.name}
          onChange={(event, value) =>
            handleDoctorChange('treatingDoctor', value ? value.id : '')
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              placeholder='Select Doctors Name'
              margin='normal'
            />
          )}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderOption={(props, item) => (
            <li {...props} key={item?.id}>
              <ListItemText>{item?.name}</ListItemText>
            </li>
          )}
        />
        {/* <TextField
          name='treatingDoctor'
          fullWidth
          margin='normal'
          size='small'
          value={formik?.values?.treatingDoctor}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          error={
            formik?.touched?.treatingDoctor &&
            Boolean(formik?.errors?.treatingDoctor)
          }
          helperText={
            formik?.touched?.treatingDoctor &&
            typeof formik?.errors?.treatingDoctor === 'string'
              ? (formik?.errors?.treatingDoctor as string)
              : ''
          }
        /> */}
      </Grid>
      <Grid item xs={12} sm={6} md={4} className={classes.dischargeFormField}>
        <label>
          Reg No
          <Asterisk />
        </label>
        <TextField
          name='treatingDoctorRegNo'
          fullWidth
          margin='normal'
          size='small'
          value={formik?.values?.treatingDoctorRegNo}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          error={
            formik?.touched?.treatingDoctorRegNo &&
            Boolean(formik?.errors?.treatingDoctorRegNo)
          }
          helperText={
            formik?.touched?.treatingDoctorRegNo &&
            typeof formik?.errors?.treatingDoctorRegNo === dataTypes?.STRING
              ? (formik?.errors?.treatingDoctorRegNo as string)
              : ''
          }
        />
      </Grid>
      {/* <Grid item xs={12} sm={6} md={4} className={classes.dischargeFormField}>
        <label>
          Signature
          <Asterisk />
        </label>
        <TextField
          name='treatingDoctorSignature'
          fullWidth
          margin='normal'
          size='small'
          value={formik?.values?.treatingDoctorSignature}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          error={
            formik?.touched?.treatingDoctorSignature &&
            Boolean(formik?.errors?.treatingDoctorSignature)
          }
          helperText={
            formik?.touched?.treatingDoctorSignature &&
            typeof formik?.errors?.treatingDoctorSignature === 'string'
              ? (formik?.errors?.treatingDoctorSignature as string)
              : ''
          }
        />
      </Grid> */}
      <Grid xs={12} mt={2} mb={2}>
        <Box
          display={'flex'}
          flexDirection={'row'}
          gap={5}
          flexWrap={'wrap'}
          justifyContent={'center'}
        >
          <Button
            className={classes?.commonBtn}
            variant='contained'
            onClick={formik?.submitForm}
          >
            Submit
          </Button>

          <Button
            className={classes?.commonBtn}
            variant='contained'
            onClick={() => openPdfInNewTab(formik?.values)}
          >
            Preview
          </Button>
        </Box>
      </Grid>
    </Grid>
  )
}

export default PatientDetailsForm
